// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel from "@vanmeter/App/Components/XngCarousel";
import { CarouselStyle } from "@vanmeter/App/Components/XngCarousel";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import Skeleton from "@vanmeter/App/Components/Skeleton/Skeleton";

interface OwnProps {
    scrollingBehavior?: "horizontal" | "vertical";
    numberOfVisibleThumbnails?: number;
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    /** @deprecated Use CarouselSlideContainer object instead. */
    carouselSlidesContainer?: InjectableCss;
    /** @deprecated Use CarouselSlide object instead. */
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
    /**
     * XNG-Changes
     */
    container?: InjectableCss;
    carouselStyle?: CarouselStyle;
    imageLink?: ClickablePresentationProps;
}

const getImageCss = (borderColor: string) => css`
    width: 100%;
    height: 100%;
    max-width: 64px;
    max-height: 64px;
    border: 1px solid ${getColor(borderColor)};
    cursor: pointer;
    justify-content: flex-start;
    img {
        max-width: 64px;
        max-height: 64px;
        padding: 3px;
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
        `,
    },
    verticalPrevArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: { width: 10 },
    verticalCarouselGridItem: { width: 12 },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 4px 3px 0;
            display: flex;
            justify-content: center;
        `,
    },
    image: {
        css: getImageCss("common.border"),
    },
    selectedImage: {
        css: getImageCss("text.main"),
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
        `,
    },
    verticalNextArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
};

export const CarouselSlidesContainer = styled.div<
    InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
>`
    display: flex;
    flex-direction: ${props => (props.isVertical ? "column" : "row")};
    align-items: ${props => (props.isVertical ? "center" : "initial")};
    ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
    ${injectCss};
`;

export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
    flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails}%;
    padding: 0;
    position: relative;
    ${injectCss};
`;

// XNG-Changes
const ImageWithSkeleton = ({ src, alt, imgProps }: { src?: string; alt?: string; imgProps?: LazyImageProps }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <>
            {!isLoaded && <Skeleton width="52px" height="52px" className="image-skeleton" />}
            <LazyImage
                {...imgProps}
                src={src}
                altText={alt}
                onLoad={() => setIsLoaded(true)}
                style={{
                    opacity: isLoaded ? 1 : 0,
                    transition: "opacity 0.25s ease",
                }}
            />
        </>
    );
};

const ProductImageCarousel = ({
    productSettings,
    productContext,
    scrollingBehavior,
    numberOfVisibleThumbnails = 6,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    // XNG-Changes
    isTabletScreen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const isVertical = scrollingBehavior === "vertical";

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.includes("?") ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images.length === 0) {
        return null;
    }

    // XNG-Changes:use XngCarousel
    return (
        <StyledWrapper {...styles.container}>
            <XngCarousel
                navigation={!isTabletScreen}
                spaceBetween={12}
                slidesPerView="auto"
                scrollbar={true}
                extendedStyles={styles.carouselStyle}
            >
                {product.images.map((productImage, index) => (
                    <StyledWrapper
                        key={productImage.id}
                        {...styles.carouselSlideInner}
                        data-test-selector="carousel_slide"
                    >
                        <Clickable
                            onClick={() => imageClickHandler(index)}
                            aria-label={productImage.imageAltText}
                            {...{ isSelected: index === selectedImageIndex }}
                            {...styles.imageLink}
                        >
                            {/* XNG-Changes */}
                            <ImageWithSkeleton
                                src={getProductImageThumbPath(productImage)}
                                alt={productImage.imageAltText}
                                imgProps={index === selectedImageIndex ? styles.selectedImage : styles.image}
                            />
                        </Clickable>
                    </StyledWrapper>
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
